// MatchState: the single mutable state tree owned by the sim (plan §8.2).
// No DOM, no Three.js, no wall clock — headless by construction.

import type { FactionId, PlayerId, SizeTag, TeamId } from '../content/types';
import type { Vec2 } from '../shared/vec';
import { SimRng } from '../shared/rng';
import { TUNING } from '../content/tuning';
import { unitDef } from '../content/registry';

export type EntId = number;

// ---------------------------------------------------------------- buffs/marks

export interface Buff {
  stat:
    | 'moveSpeed'
    | 'attackSpeed'
    | 'damage'
    | 'damageTaken'
    | 'rangedDamageTaken'
    | 'structureDamage'
    | 'repairResist';
  mult: number;
  remaining: number; // seconds; Infinity = permanent
  /** Stable source key for continuously refreshed effects that must not stack with themselves. */
  source?: string;
}

export interface Mark {
  markId: string;
  remaining: number;
}

// ---------------------------------------------------------------- commands (high-level)

export type HLKind =
  | 'idle'
  | 'move'
  | 'attack'
  | 'capture'
  | 'defend'
  | 'patrol'
  | 'raid'
  | 'escort'
  | 'withdraw';

export interface HLCommand {
  kind: HLKind;
  targetPos?: Vec2;
  targetEntity?: EntId;
  /** defend: center of patrol ring; patrol: pointA/pointB */
  pointA?: Vec2;
  pointB?: Vec2;
  patrolLegToB?: boolean;
  /** set automatically by Capture→Defend conversion */
  auto?: boolean;
  /** defend around a site id (patrol its ring) */
  siteId?: string;
}

// ---------------------------------------------------------------- entities

export interface Telegraph {
  abilityId: string;
  remaining: number;
  targetId?: EntId;
}

export interface Squad {
  id: EntId;
  defId: string;
  owner: PlayerId;
  pos: Vec2;
  prevPos: Vec2; // for render interpolation
  /** Last position with open clearance, used to eject from a wall/gate on the side entered. */
  lastOpenPos: Vec2;
  facing: number;
  health: number;
  maxHealth: number;
  tempHealth: number;
  tempHealthRemaining: number;
  buffs: Buff[];
  marks: Mark[];
  command: HLCommand;
  /** Original order temporarily suspended while the squad breaches a dynamic blocker. */
  breachResume: { command: HLCommand; goal: Vec2 } | null;
  path: Vec2[] | null;
  pathIndex: number;
  /** position the current path was computed for */
  pathGoal: Vec2 | null;
  /** nav obstacle revision used by the route; wakes exhausted partial paths after a breach */
  pathNavRevision: number;
  repathCooldown: number;
  /** seconds spent barely moving while wanting to move (stuck detection) */
  stuckTime: number;
  /** Goal associated with bestRouteRemaining; resets progress history on a new order. */
  progressGoal: Vec2 | null;
  /** Best remaining route length reached for progress detection; prevents lateral oscillation. */
  bestRouteRemaining: number;
  /** Nav revision and route signature used to segment legitimate material detours. */
  progressNavRevision: number;
  progressRouteSignature: number;
  /** where the command layer wants the squad this tick (null = hold) */
  moveTarget: Vec2 | null;
  /** sim tick of the latest explicit order; used before command steering refreshes */
  commandIssuedTick: number;
  attackCooldown: number;
  targetId: EntId | null;
  retargetTimer: number;
  /**
   * Attack wind-up: seconds remaining before the committed swing lands. Damage no longer fires
   * the instant the cooldown expires; the attacker telegraphs first, and the blow can miss if
   * the target dies or leaves range during the window.
   */
  windupRemaining: number;
  /** the target the swing was committed against, re-validated when the wind-up completes */
  windupTargetId: EntId | null;
  /** sim time the current targetId was acquired; drives futile-chase target switching */
  targetSince: number;
  lastDamagedAt: number; // sim time seconds, -inf initially
  lastCombatAt: number;
  abilityCooldowns: Record<string, number>;
  abilityFiredOnce: Record<string, boolean>;
  telegraph: Telegraph | null;
  escortOf: EntId | null;
  escortSlot: Vec2 | null;
  /** garrisoned on a castle wall: untargetable, immobile, +25% range */
  mountedOnWall: EntId | null;
  /** mounted on a host squad (Crown Colossus, Groveback): melee-untargetable, slaved position */
  mountedOnSquad: EntId | null;
  /** rooted in place (Grasping Roots, Circle of Thorns): speed 0, ranged still fires */
  rootedRemaining: number;
  /**
   * Group speed matching: while a formation order is in flight, every member is capped at the
   * group's slowest base speed so the army arrives together instead of stringing out. Cleared
   * on arrival, on engaging, and by any non-formation order.
   */
  groupSpeedCap: number | null;
  /** temporary physical knockdown: movement and combat are disabled while the render pose ragdolls */
  knockdownRemaining: number;
  knockdownTotal: number;
  /** temporary squad (grave shades, Last Battalion): expires without death effects */
  temporaryRemaining: number | null;
  /** Core Annex System: annex upgrade id that grew this unit (max-alive accounting) */
  sourceAnnexId: string | null;
  /** does not count against Command (Astral Echoes) */
  commandFree: boolean;
  /** Astral Echo: temporary copy — cannot capture, halved structure damage via buffs */
  isEcho: boolean;
  /** Astral Echo: fizzles if this conduit dies within the window */
  echoLink: { conduitId: EntId; remaining: number } | null;
  /** Rootwarden guardian stance (E: Take Root) — immobile, auras + thorn volley active */
  treeRooted: boolean;
  /** E: Uproot channel; immobile until it reaches 0 */
  uprootRemaining: number;
  /** economy aura disabled after uprooting */
  auraSuppressedRemaining: number;
  /** healing/recovery taken multiplier (Seal of Rot) with its remaining duration */
  healingTakenMult: number;
  healingTakenRemaining: number;
  /** attacks landed on structures (nthAttackVsStructure trigger) */
  structureAttackCount: number;
  /** in weapon range of its target and holding position (movement hysteresis, anti-jitter) */
  engaged: boolean;
  /** active charge dash (giant charge ability); null when not charging */
  charge: {
    targetId: EntId;
    remaining: number;
    speedMult: number;
    impactDamage: number;
    impactSlowMult: number;
    impactSlowSeconds: number;
    impactKnockback: number;
    impactKnockdownSeconds: number;
  } | null;
  isBoss: boolean;
  dead: boolean;
}

export type WorkerFsm =
  | 'idle'
  | 'toDeposit'
  | 'gathering'
  | 'toDropOff'
  | 'toBuild'
  | 'building'
  | 'toRepair'
  | 'repairing'
  | 'fleeing'
  | 'toShelter'
  | 'sheltered';

export interface WorkerEnt {
  id: EntId;
  /** unit def of this worker (faction worker or a worker variant like Master Builder) */
  defId: string;
  /** hub the worker belongs to (Core or the site tower that trained it) — gathers only in that zone */
  homeId: EntId | null;
  owner: PlayerId;
  pos: Vec2;
  prevPos: Vec2;
  /** Last position with open clearance, used to recover from dynamic nav changes. */
  lastOpenPos: Vec2;
  health: number;
  maxHealth: number;
  state: WorkerFsm;
  carrying: { type: 'power' | 'essence'; amount: number } | null;
  depositId: EntId | null;
  buildTargetId: EntId | null;
  repairTargetId: EntId | null;
  path: Vec2[] | null;
  pathIndex: number;
  /** Net no-progress time while the worker is actively following a route. */
  stuckTime: number;
  /** Consecutive stuck-recovery side-steps without net progress; escalates to re-tasking. */
  recoveries: number;
  gatherTimer: number;
  fleeTimer: number;
  lastDamagedAt: number;
  dead: boolean;
  /** beyond the home zone's worker cap this trip — gathers at half rate */
  overCap: boolean;
  /** Conduit Nexus transfer: materializing at the destination, inert and vulnerable */
  arrivalRemaining: number;
  /** Deep Recall: channel before burrowing to the Core shelter; damage interrupts */
  recallChannelRemaining: number;
}

export type BuildingState = 'blueprint' | 'constructing' | 'active' | 'disabled' | 'destroyed';

export interface QueueItem {
  unitId: string;
  remaining: number;
  total: number;
  costPower: number;
  costEssence: number;
  costCommand: number;
}

export interface Building {
  id: EntId;
  defId: string;
  owner: PlayerId;
  pos: Vec2;
  state: BuildingState;
  /** toggleable building mode (Cruel Production etc.), off by default */
  modeActive: boolean;
  health: number;
  maxHealth: number;
  tempHealth: number;
  tempHealthRemaining: number;
  buildProgress: number; // 0..1
  queue: QueueItem[];
  autoProduce: Record<string, boolean>;
  rally: Vec2;
  upgradeDone: boolean;
  upgradeRemaining: number; // 0 = not researching
  /** multi-upgrade track (Bastion annexes): completed ids + the one in research */
  upgradesDone: string[];
  upgradeInProgress: { id: string; remaining: number } | null;
  attackCooldown: number;
  targetId: EntId | null;
  lastDamagedAt: number;
  lastSiegeDamagedAt: number;
  healOverTime: { remaining: number; perSecond: number }[];
  /** site the building belongs to, or null for core territory */
  siteId: string | null;
  isCore: boolean;
  lastCourseFired: boolean;
  cardId: string | null; // card that created it (frees active copy on death)
  repairResist: number; // 0..1 (Last Course of Stone)
  /** timed repair halving (Kingbreaker Hammer) */
  repairHalvedRemaining: number;
  /** remaining Toll of Iron marks (Oathbound Gate) */
  gateMarksRemaining: number;
  /** temporary structure lifetime (saplings, erupted groves); null = permanent */
  expiresRemaining: number | null;
  /** fire suppresses Verdant regeneration (20 s per hit) */
  fireSuppressedRemaining: number;
  /** generic accumulator (Soul Mill corpse counter) */
  counter: number;
  /** site towers: worker training */
  workerAuto: boolean;
  workerQueueRemaining: number | null;
  /** worker hubs: this zone's workers are recalled to shelter (per-base recall) */
  recallActive: boolean;
  /** footprint currently stamped into the nav grid (walls defer until construction completes) */
  navStamped: boolean;
  /** walls: centered gate aperture (cells along the long axis); 0 = solid */
  gateSpan: number;
  /** automatic door target: true while friendly traffic requests an opening */
  gateOpen: boolean;
  /** actual shared navigation state; changes only once the doors are visually clear */
  gateNavOpen: boolean;
  /** deterministic presentation progress (0 closed, 1 fully open) */
  gateOpenFraction: number;
  /** minimum time the open gate remains available after traffic clears */
  gateHoldRemaining: number;
  /** footprint orientation: 0 = def footprint as-is, 1 = w/h swapped */
  rot: 0 | 1;
  /** Core Annex System: annex upgrade id this structure came from (deployed forts, cocoon) */
  annexSourceId: string | null;
  /** replaced annex structure collapsing; destroyed at 0 */
  witherRemaining: number | null;
  /** building spawner (Grave Beacon): time until the next wave attempt */
  spawnCooldown: number;
  /** cooldowns/counters for data-driven non-Core building traits */
  traitCooldowns: Record<string, number>;
  traitCounters: Record<string, number>;
  /** newest death timestamp consumed by death-reactive traits */
  lastTraitDeathAt: number;
  /** capture-triggered training boost remaining */
  captureTrainingRemaining: number;
}

/** Building footprint in world cells, honoring its orientation. */
export function footprintOf(def: { footprint: { w: number; h: number } }, rot: 0 | 1): { w: number; h: number } {
  return rot === 1 ? { w: def.footprint.h, h: def.footprint.w } : def.footprint;
}

/** A boss card mid-assembly (announced summon sequence). */
export interface PendingSummon {
  defId: string;
  cardId: string;
  owner: PlayerId;
  pos: Vec2;
  remaining: number;
  total: number;
}

/** Corpse mark left by a qualifying death (Ashen economy substrate). */
export interface CorpseMark {
  id: EntId;
  pos: Vec2;
  remaining: number;
  /** owner of the squad that died */
  sourceOwner: PlayerId;
}

/** A ritual being channeled through its destructible standard. */
export interface RitualState {
  /** the standard building keeping the channel alive */
  standardId: EntId;
  ritualId: string;
  cardId: string;
  owner: PlayerId;
  remaining: number;
}

/** Recent squad death (allyDiedNearby / escortSquadDied triggers, Night of the Unburied). */
export interface DeathRecord {
  defId: string;
  owner: PlayerId;
  pos: Vec2;
  at: number; // sim time
  wasBoss: boolean;
  escortOf: EntId | null;
}

/** Ancient ruin / wall foundation: claimable into a castle wall. */
export interface Ruin {
  id: EntId;
  pos: Vec2;
  /** the wall built on it, if any */
  buildingId: EntId | null;
  /** sim time before which a destroyed wall spot cannot be rebuilt */
  cooldownUntil: number;
  /** power cost to raise the wall here */
  cost: number;
  /** centered gate aperture (cells along the long axis); 0 = solid */
  gateSpan: number;
  /** footprint orientation: 0 = def footprint as-is, 1 = w/h swapped */
  rot: 0 | 1;
  /** grouped ruins (base ring) raise together via BuildBaseWall, not individually */
  group: string | null;
}

export interface Deposit {
  id: EntId;
  type: 'power' | 'essence';
  pos: Vec2;
  amount: number;
  initialAmount: number;
  gatherSlots: number;
}

export interface SiteState {
  id: string;
  name: string;
  pos: Vec2;
  owner: PlayerId | -1;
  /** capture meter toward new ownership (0..100), owned by meterPlayer */
  meter: number;
  meterPlayer: PlayerId | -1;
  /** current owner's control (100 when securely owned; neutralize drains it) */
  control: number;
  contested: boolean;
  decayGrace: number;
}

export interface Projectile {
  id: EntId;
  owner: PlayerId;
  pos: Vec2;
  prevPos: Vec2;
  targetId: EntId;
  targetKind: 'squad' | 'worker' | 'building' | 'mine';
  speed: number;
  damage: number;
  isRanged: true;
  structureMult: number;
  sourceSquadId: EntId | null;
  dead: boolean;
}

export interface Mine {
  id: EntId;
  /** trap def that deployed this piece */
  defId: string;
  /** card that deployed the field (active-copy accounting) */
  cardId: string | null;
  owner: PlayerId;
  fieldId: number;
  pos: Vec2;
  health: number;
  built: boolean;
  buildProgress: number;
  armDelay: number;
  triggered: boolean;
  dead: boolean;
}

export interface Zone {
  id: EntId;
  owner: PlayerId;
  spellId: string;
  pos: Vec2;
  radius: number;
  remaining: number;
  /** Judgment Barrage: telegraph countdown, then timed impacts */
  telegraphRemaining?: number;
  impactsRemaining?: number;
  impactTimer?: number;
  /** lane spells (Wild Stampede): unit direction + extent */
  lane?: { dir: Vec2; length: number; width: number };
  /** lane assault resolved flag */
  laneResolved?: boolean;
}

// ---------------------------------------------------------------- player

export type CardUiState =
  | 'available'
  | 'noUses'
  | 'cooldown'
  | 'activeLimit'
  | 'wrongAscension'
  | 'insufficientCommand'
  | 'insufficientEssence'
  | 'insufficientPower'
  | 'noLocation';

export interface CardRuntime {
  cooldownRemaining: number;
  usesRemaining: number; // Infinity for reusable
  activeCopies: number;
}

/** Core Annex System per-player runtime (socketed annex behaviors). */
export interface AnnexState {
  /** upgradeId → seconds until the next spawn attempt */
  spawnTimers: Record<string, number>;
  /** upgradeId → finished spawn held while Command is full (one per annex) */
  stored: Record<string, string>;
  /** Sanguine Crucible */
  titheActive: boolean;
  titheTimer: number;
  souls: number;
  titheWarned: boolean;
  apotheosisForm: 'amalgam' | 'sovereign';
  apotheosis: { buildingId: EntId; remaining: number } | null;
  apotheosisUsed: boolean;
  /** Echo Observatory */
  echoCharge: number;
  echoProgress: number;
  echoArmed: boolean;
  /** action cooldowns */
  deepRecallCooldown: number;
  transferCooldown: number;
  deathSaveCooldown: number;
  /** Phase Barrier regenerating shield on the Core */
  coreShield: number;
  /** Grim Industry stack expiry times (remaining seconds each) */
  grimStacks: number[];
}

export function makeAnnexState(): AnnexState {
  return {
    spawnTimers: {},
    stored: {},
    titheActive: false,
    titheTimer: 0,
    souls: 0,
    titheWarned: false,
    apotheosisForm: 'amalgam',
    apotheosis: null,
    apotheosisUsed: false,
    echoCharge: 0,
    echoProgress: 0,
    echoArmed: true,
    deepRecallCooldown: 0,
    transferCooldown: 0,
    deathSaveCooldown: 0,
    coreShield: 0,
    grimStacks: [],
  };
}

export interface PlayerState {
  id: PlayerId;
  team: TeamId;
  faction: FactionId;
  isAI: boolean;
  power: number;
  essence: number;
  allocationPower: number; // 0..100
  autoWorker: boolean;
  workforceUsed: number;
  workforceCap: number;
  commandUsed: number;
  commandCap: number;
  ascensionDiscovered: 1 | 2 | 3 | 4;
  ascensionPlayable: 1 | 2 | 3 | 4;
  cards: Record<string, CardRuntime>;
  deck: string[];
  shelterOccupants: EntId[];
  shelterCapacity: number;
  shelterReleaseTimer: number;
  workerQueue: { remaining: number }[];
  noEssenceWarning: boolean;
  /** per-ability cooldowns for the faction Core defense (coreDefense.ts) */
  coreDefenseCooldowns: Record<string, number>;
  /** stored corpse marks at the Core (Ashen; capped) */
  corpseStored: number;
  /** The Great Awakening: buildings regenerate through light damage while > 0 */
  awakeningRemaining: number;
  /** Devoured Mass (Devouring Gate): each stack discounts the next boss 12%, max 2 */
  devouredMass: number;
  /** Convergence Protocol: perfect-network window (faster production, conduit summons) */
  convergenceRemaining: number;
  /** Core Annex System runtime (socketed annex behaviors) */
  annex: AnnexState;
  stats: {
    unitsBuilt: number;
    unitsLost: number;
    sitesCaptured: number;
    coreDamageDealt: number;
    powerGathered: number;
    essenceGathered: number;
    cardsPlayed: number;
  };
}

// ---------------------------------------------------------------- events

export type SimEvent =
  | { type: 'SiteCaptured'; site: string; owner: PlayerId }
  | { type: 'SiteLost'; site: string; loser: PlayerId }
  | { type: 'SiteNeutralized'; site: string }
  | { type: 'AscensionUnlocked'; player: PlayerId; tier: number }
  | { type: 'CardPlayed'; player: PlayerId; cardId: string }
  | { type: 'CardDenied'; player: PlayerId; cardId: string; reason: string }
  | { type: 'SquadDied'; id: EntId; owner: PlayerId; pos: Vec2; wasBoss: boolean; defId: string }
  | { type: 'WorkerDied'; id: EntId; owner: PlayerId; pos: Vec2; defId: string }
  | { type: 'BuildingDestroyed'; id: EntId; owner: PlayerId; pos: Vec2; defId: string }
  | { type: 'BuildingComplete'; id: EntId; owner: PlayerId; defId: string }
  | { type: 'GateOpened'; id: EntId; owner: PlayerId; pos: Vec2 }
  | { type: 'GateClosed'; id: EntId; owner: PlayerId; pos: Vec2 }
  | { type: 'UpgradeComplete'; id: EntId; owner: PlayerId; pos: Vec2; name: string }
  | { type: 'BarrageImpact'; pos: Vec2 }
  | { type: 'AbilityTriggered'; id: EntId; abilityId: string; pos: Vec2; label: string }
  | { type: 'GiantCrush'; giantId: EntId; victimDefId: string; pos: Vec2 }
  | { type: 'BossSummoned'; player: PlayerId; defId: string; pos: Vec2 }
  | { type: 'CoreDamaged'; player: PlayerId; healthFraction: number }
  | { type: 'TrapTriggered'; pos: Vec2 }
  | { type: 'RaidWarning'; player: PlayerId; pos: Vec2 }
  | { type: 'MatchEnded'; winningTeam: TeamId | -1 }
  | { type: 'ProjectileHit'; pos: Vec2 }
  | {
      type: 'MeleeImpact';
      pos: Vec2;
      weight: 'light' | 'heavy' | 'giant';
      structure: boolean;
    }
  | { type: 'SquadSpawned'; id: EntId; owner: PlayerId }
  | { type: 'SquadTeleported'; id: EntId; from: Vec2; to: Vec2 }
  | { type: 'SquadKnockedDown'; id: EntId; owner: PlayerId; pos: Vec2; duration: number }
  | { type: 'BossSummoning'; player: PlayerId; defId: string; pos: Vec2; seconds: number; global: boolean }
  | { type: 'RitualStarted'; player: PlayerId; ritualId: string; pos: Vec2 }
  | { type: 'RitualComplete'; player: PlayerId; ritualId: string; pos: Vec2 }
  | { type: 'RitualCancelled'; player: PlayerId; ritualId: string; pos: Vec2 };

// ---------------------------------------------------------------- match state

export type MatchPhase = 'countdown' | 'running' | 'ended';

export interface MatchState {
  tick: number;
  time: number; // seconds since match start (excludes countdown)
  phase: MatchPhase;
  countdownRemaining: number;
  winningTeam: TeamId | -1;
  seed: number;
  rng: SimRng;
  nextId: EntId;
  players: PlayerState[];
  squads: Map<EntId, Squad>;
  workers: Map<EntId, WorkerEnt>;
  buildings: Map<EntId, Building>;
  deposits: Map<EntId, Deposit>;
  projectiles: Map<EntId, Projectile>;
  mines: Map<EntId, Mine>;
  zones: Map<EntId, Zone>;
  ruins: Ruin[];
  sites: SiteState[];
  corpseMarks: CorpseMark[];
  rituals: RitualState[];
  pendingSummons: PendingSummon[];
  /** conduit buildings currently linked to their owner's Core (recomputed each tick) */
  connectedConduits: Set<EntId>;
  /** ring buffer of recent squad deaths (newest last, capped) */
  recentDeaths: DeathRecord[];
  events: SimEvent[];
  /** monotonically increasing minefield deployment counter */
  nextFieldId: number;
  paused: boolean;
}

// ---------------------------------------------------------------- stat helpers

export function armorReductionOf(band: string): number {
  return TUNING.armorReduction[band] ?? 0;
}

export function buffMult(buffs: Buff[], stat: Buff['stat']): number {
  let m = 1;
  for (const b of buffs) if (b.stat === stat) m *= b.mult;
  return m;
}

export function squadMoveSpeed(s: Squad): number {
  if (s.rootedRemaining > 0) return 0;
  // Rootwarden guardian stance: immobile while rooted and through the uproot channel
  if (s.treeRooted || s.uprootRemaining > 0) return 0;
  const d = unitDef(s.defId);
  let v = d.moveSpeed * buffMult(s.buffs, 'moveSpeed');
  if (s.command.kind === 'withdraw') v *= TUNING.withdrawSpeedBonus;
  // Marching in formation: hold to the group's pace. Applied before the withdraw bonus would be
  // wrong — a retreat should always be able to outrun the formation cap — so it is applied here,
  // after, and withdraw clears the cap anyway (see stepMovement).
  if (s.groupSpeedCap !== null) v = Math.min(v, s.groupSpeedCap);
  return v;
}

export function squadMembers(s: Squad): number {
  const d = unitDef(s.defId);
  return Math.max(1, Math.ceil((s.health / s.maxHealth) * d.squadMemberCount));
}

export function hasMark(marks: Mark[], id: string): boolean {
  return marks.some((m) => m.markId === id && m.remaining > 0);
}

export function sizeIs(defId: string, tags: SizeTag[]): boolean {
  return tags.includes(unitDef(defId).sizeTag);
}
